// components
import { styled, Box, BoxProps } from '@mui/material';

// enums
import { ToolbarFlowEnum } from '../ToolbarWrapper.enums';

// types
import type { ToolbarFlowType } from '../ToolbarSet.types';

export const StyledToolbarContainer = styled(Box, {
  shouldForwardProp: (prop: string) =>
    ![
      'isRightPositioned',
      'isZenPositioned',
      'zenOpen',
      'showDateNavigation',
      'toolbarFlow'
    ].includes(prop)
})<
  BoxProps & {
    isRightPositioned?: boolean;
    isZenPositioned?: boolean;
    zenOpen?: boolean;
    showDateNavigation?: boolean;
    toolbarFlow?: ToolbarFlowType;
  }
>(({
  theme,
  isRightPositioned,
  isZenPositioned,
  zenOpen,
  showDateNavigation = true,
  toolbarFlow = ToolbarFlowEnum.GRID
}) => {
  const isPositioned = isRightPositioned || isZenPositioned;
  const isFlex = toolbarFlow === ToolbarFlowEnum.FLEX;
  const base = {
    display: isFlex ? 'flex' : isPositioned ? 'flex' : 'grid',
    flexDirection: isPositioned ? 'column' : 'row',
    gridTemplateColumns: showDateNavigation ? '1fr 1fr 1fr' : '1fr',
    zIndex: isZenPositioned ? 1000 : 1,
    border: `1px solid ${theme.vars.palette.outline.variant}`,
    borderRadius: theme.spacing(4),
    padding: theme.spacing(1, isPositioned ? 1 : 3),
    backgroundColor: theme.palette.common.white,
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.04)',
    height: 'fit-content',
    alignItems: 'center',
    flexWrap: 'wrap',
    gridTemplateAreas: showDateNavigation
      ? `'leftContent dateNavigation rightContent'`
      : `'leftContent rightContent'`,

    '& > :nth-of-type(1)': {
      gridArea: 'leftContent'
    },
    '& > :nth-of-type(2)': {
      gridArea: 'dateNavigation'
    },
    '& > :nth-of-type(3)': {
      gridArea: 'rightContent'
    },

    [theme.breakpoints.down('medium')]: {
      gridTemplateColumns: '1fr 1fr',
      gridTemplateAreas: `
      'dateNavigation dateNavigation'
      'leftContent rightContent'
    `
    }
  } as const;

  if (!isZenPositioned) {
    return {
      position: 'relative',
      ...base
    };
  }

  return {
    position: 'absolute',
    right: 0,
    ...base,
    transform: zenOpen ? 'translateX(0)' : 'translateX(calc(100% + 16px))',
    transition: 'transform 220ms ease',
    pointerEvents: zenOpen ? 'auto' : 'none'
  };
});

export const StyledActionStripBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'justifyEnd' && prop !== 'isVertical'
})<BoxProps & { justifyEnd?: boolean; isVertical?: boolean }>(
  ({ theme, justifyEnd, isVertical }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    ...(justifyEnd ? { marginLeft: isVertical ? 0 : 'auto' } : {}),
    ...(isVertical ? { flexDirection: 'column' } : {})
  })
);
